/**
 * InspectionRangementHubDialog — Hub central pour les inspections de rangement.
 *
 * Ouvre depuis un bouton sur la page Unités. Contient :
 *   1. Onglet Historique global : toutes les inspections de toutes les unités
 *      (avec filtres par unité, par chef d'équipe, par statut)
 *   2. Onglet Paramètres : config de la fréquence (admin only)
 *   3. Onglet Critères : modifier la liste des critères de rangement (admin only)
 *
 * Centralise tout ce qui était auparavant dispersé entre "Inspection > Critères"
 * et "Inspection > Rangement". La page Inspection ne s'occupe plus que des bons
 * de travail.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { History, Settings, ListChecks, CheckCircle2, XCircle, Calendar, User as UserIcon, Plus, Edit3, Trash2, GripVertical, Save } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function InspectionRangementHubDialog({ open, onOpenChange, canEdit }) {
  const [tab, setTab] = useState('historique');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-emerald-600" /> Inspections de rangement
          </DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="historique" data-testid="tab-hub-historique">
              <History className="w-3.5 h-3.5 mr-1" /> Historique global
            </TabsTrigger>
            {canEdit && (
              <>
                <TabsTrigger value="criteres" data-testid="tab-hub-criteres">
                  <ListChecks className="w-3.5 h-3.5 mr-1" /> Critères
                </TabsTrigger>
                <TabsTrigger value="parametres" data-testid="tab-hub-parametres">
                  <Settings className="w-3.5 h-3.5 mr-1" /> Paramètres
                </TabsTrigger>
              </>
            )}
          </TabsList>

          <TabsContent value="historique" className="mt-4">
            <HistoriqueGlobal />
          </TabsContent>
          {canEdit && (
            <>
              <TabsContent value="criteres" className="mt-4">
                <CriteresEditor />
              </TabsContent>
              <TabsContent value="parametres" className="mt-4">
                <ParametresFrequence />
              </TabsContent>
            </>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ─── Sous-composants ────────────────────────────────────────────────────────

function HistoriqueGlobal() {
  const [inspections, setInspections] = useState([]);
  const [unites, setUnites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUnite, setFilterUnite] = useState('');
  const [filterStatut, setFilterStatut] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const [insRes, unitesRes] = await Promise.all([
          axios.get(`${API_URL}/api/inspections/rangement`),
          axios.get(`${API_URL}/api/vehicules`),
        ]);
        setInspections(insRes.data?.inspections || []);
        setUnites(unitesRes.data?.vehicules || []);
      } catch {
        toast.error('Erreur chargement historique');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = inspections.filter(i => {
    if (filterUnite && i.unite_rangement_id !== filterUnite) return false;
    if (filterStatut === 'conforme' && !i.reussie) return false;
    if (filterStatut === 'nonconforme' && i.reussie) return false;
    return true;
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <select
          value={filterUnite}
          onChange={e => setFilterUnite(e.target.value)}
          className="border rounded-md px-2 py-1.5 bg-white text-sm"
          data-testid="filter-unite-historique"
        >
          <option value="">Toutes les unités</option>
          {unites.map(u => <option key={u.id} value={u.id}>{u.nom}</option>)}
        </select>
        <div className="flex gap-1">
          {[
            { k: 'all', label: 'Tous' },
            { k: 'conforme', label: 'Conforme' },
            { k: 'nonconforme', label: 'Non conforme' },
          ].map(c => (
            <button
              key={c.k}
              onClick={() => setFilterStatut(c.k)}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition ${
                filterStatut === c.k
                  ? c.k === 'conforme' ? 'bg-emerald-600 text-white border-emerald-600'
                    : c.k === 'nonconforme' ? 'bg-red-600 text-white border-red-600'
                    : 'bg-slate-700 text-white border-slate-700'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
              data-testid={`chip-statut-${c.k}`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <span className="ml-auto text-xs text-slate-500">{filtered.length} résultat(s)</span>
      </div>

      {loading ? (
        <p className="text-center py-8 text-slate-400 text-sm">Chargement...</p>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-slate-400">
          <History className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Aucune inspection trouvée.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {filtered.map(insp => (
            <Card key={insp.id} className={`border-l-4 ${insp.reussie ? 'border-l-emerald-500' : 'border-l-red-500'}`}>
              <CardContent className="pt-3 pb-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    {insp.reussie
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      : <XCircle className="w-4 h-4 text-red-600 shrink-0" />}
                    <div>
                      <p className="font-medium text-sm">
                        {insp.unite_rangement_nom ? `Unité : ${insp.unite_rangement_nom}` : 'Unité non spécifiée'}
                      </p>
                      <p className="text-xs text-slate-500 flex items-center gap-1 flex-wrap">
                        <Calendar className="w-3 h-3" />
                        {new Date(insp.date_inspection + (insp.date_inspection?.includes('T') ? '' : 'T12:00:00')).toLocaleDateString('fr-CA', { day: 'numeric', month: 'short', year: 'numeric' })}
                        <span className="text-slate-300">·</span>
                        <UserIcon className="w-3 h-3" /> {insp.inspecteur_nom || 'N/A'}
                        {insp.chef_equipe_nom && (
                          <>
                            <span className="text-slate-300">·</span>
                            Chef : <span className="font-medium text-slate-700">{insp.chef_equipe_nom}</span>
                          </>
                        )}
                        <span className="text-slate-300">·</span>
                        {insp.nb_reussi}/{insp.nb_total}
                        {insp.nb_echoue > 0 && <span className="text-red-500">· {insp.nb_echoue} échec(s)</span>}
                      </p>
                    </div>
                  </div>
                  <Badge className={insp.reussie ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}>
                    {insp.reussie ? 'Conforme' : 'Non conforme'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function ParametresFrequence() {
  const [freq, setFreq] = useState(14);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/inspections/rangement/config`)
      .then(r => { setFreq(r.data?.frequence_jours || 14); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    const v = parseInt(freq, 10);
    if (!v || v < 1 || v > 365) { toast.error('Fréquence invalide (1 à 365 jours)'); return; }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/inspections/rangement/config`, { frequence_jours: v });
      toast.success('Fréquence mise à jour');
    } catch {
      toast.error('Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-amber-50 border-amber-200">
      <CardContent className="pt-4 pb-4 space-y-3">
        <div>
          <p className="text-sm font-semibold text-amber-900">Fréquence des inspections de rangement</p>
          <p className="text-xs text-amber-700">
            Une bannière de rappel apparaîtra sur chaque unité dont la dernière inspection dépasse ce seuil.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="number" min={1} max={365}
            value={freq}
            disabled={!loaded}
            onChange={e => setFreq(parseInt(e.target.value, 10) || 1)}
            className="w-24 h-9 bg-white"
            data-testid="input-frequence-jours"
          />
          <span className="text-xs text-amber-800">jours</span>
          <Button size="sm" onClick={save} disabled={saving || !loaded} data-testid="btn-save-frequence">
            {saving ? 'Sauvegarde...' : 'Enregistrer'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function CriteresEditor() {
  const [criteres, setCriteres] = useState([]);
  const [newLabel, setNewLabel] = useState('');
  const [editingIdx, setEditingIdx] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/inspections/criteres/rangement_unites`);
      setCriteres(r.data?.criteres || []);
    } catch {
      toast.error('Erreur chargement critères');
    }
  };
  useEffect(() => { reload(); }, []);

  const persist = async (newList) => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/inspections/criteres/rangement_unites`, { criteres: newList });
      setCriteres(newList);
      toast.success('Critères mis à jour');
    } catch {
      toast.error('Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = () => {
    if (!newLabel.trim()) return;
    const list = [...criteres, { label: newLabel.trim() }];
    setNewLabel('');
    persist(list);
  };
  const handleEditSave = (idx) => {
    const list = [...criteres];
    list[idx] = { ...list[idx], label: editValue };
    setEditingIdx(null);
    persist(list);
  };
  const handleDelete = (idx) => persist(criteres.filter((_, i) => i !== idx));

  return (
    <Card>
      <CardContent className="pt-4">
        <p className="text-xs text-slate-500 mb-3">
          Liste des critères vérifiés à chaque inspection de rangement d'unité.
        </p>
        <div className="space-y-2">
          {criteres.map((c, idx) => (
            <div key={c.id || idx} className="flex items-center gap-2 p-2 rounded-lg border bg-slate-50 group">
              <GripVertical className="w-4 h-4 text-slate-300 shrink-0" />
              <span className="text-xs text-slate-400 w-6 shrink-0 font-mono">{idx + 1}.</span>
              {editingIdx === idx ? (
                <div className="flex-1 flex gap-2">
                  <Input value={editValue} onChange={e => setEditValue(e.target.value)} className="flex-1 h-8 text-sm" autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') handleEditSave(idx); if (e.key === 'Escape') setEditingIdx(null); }} />
                  <Button size="sm" className="h-8 px-3" onClick={() => handleEditSave(idx)} disabled={saving}>
                    <Save className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setEditingIdx(null)}>✕</Button>
                </div>
              ) : (
                <>
                  <span className="flex-1 text-sm text-slate-700">{c.label}</span>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-500"
                      onClick={() => { setEditingIdx(idx); setEditValue(c.label); }}>
                      <Edit3 className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" onClick={() => handleDelete(idx)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </>
              )}
            </div>
          ))}
          <div className="flex gap-2 pt-2 border-t mt-3">
            <Input placeholder="Nouveau critère..." value={newLabel}
              onChange={e => setNewLabel(e.target.value)} className="flex-1 text-sm"
              onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }} />
            <Button onClick={handleAdd} disabled={!newLabel.trim() || saving} size="sm">
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
